import { useState, useEffect, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
import {
  GetMessageWithIntl,
  MessageProps,
} from '~/shared/components/parts/Message/Message';
import {
  ModalDialogComponent,
  ModalDialogComponentProps,
} from '../ModalDialog/ModalDialog';
import { CaptionButton } from '~/shared/components/ui/Button/CaptionButton';
import { Textbox } from '~/shared/components/ui/Textbox';
import { Textarea } from '~/shared/components/ui/Textarea';
import {
  DataFilterbox,
  DataFilterboxItem,
} from '~/shared/components/ui/Filterbox';
import { ConfirmationDialog } from '~/shared/components/ui/Dialog';
import { validateEmail } from '~/shared/components/parts/validator';
import { formatUserWIthEmail, includeInputValidateError } from '~/shared/utils';
import { sharelib } from '~/shared/libs/clientsdk';
import './base.css';
import './RelationRequestDialog.css';

const EMAIL_REG_EXP = new RegExp('[^@]+@[^@]+');

export interface RelationRequestDialogMessageOption {
  headerLabelId: MessageProps;
  messageLabelId?: MessageProps;
}

/**
 * 入力項目に関する項目用インターフェイス
 */
export interface RelationRequestDialogInputOption {
  businessUnitManagementId: string;
  companyName: string;
  tenantAdministrators: sharelib.IUserReference[];
}

export interface RelationRequestDialogResult {
  recipient: sharelib.IUserReference;
  contact: string;
  contactEmail: string;
  contactDepartment: string;
  message: string;
  contactInformationMessage: string;
}

export interface RelationRequestDialogProps {
  isOpen: boolean;
  messageOption: RelationRequestDialogMessageOption;
  inputOption: RelationRequestDialogInputOption;
  onDecision: (result: RelationRequestDialogResult) => void;
  onCancel: () => void;
}

export function RelationRequestDialog(props: RelationRequestDialogProps) {
  const intl = useIntl();

  const { isOpen, messageOption, inputOption, onCancel, onDecision } = props;

  const [recipient, setRecipient] = useState<DataFilterboxItem[]>([]);
  const [contact, setContact] = useState<string>('');
  const [contactEmail, setContactEmail] = useState<string>('');
  const [contactDepartment, setContactDepartment] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [contactInformationMessage, setContactInformationMessage] =
    useState<string>('');
  const [preview, setPreview] = useState<string>();
  const [isSendConfirmOpen, setSendConfirmOpen] = useState(false);

  const administratorItems: DataFilterboxItem[] = useMemo(
    () =>
      inputOption.tenantAdministrators.map((v) => ({
        value: v.email ?? '',
        displayName: formatUserWIthEmail(v.displayName, v.email),
      })),
    [inputOption.tenantAdministrators]
  );

  const requiredInputArea = useRef(null);
  const requiredContactArea = useRef(null);
  const requiredContactEmailArea = useRef(null);
  const requiredContactInformationMessageArea = useRef(null);

  const previewMessage =
    '{companyName}\n' +
    '{recipientName}様\n' +
    '\n' +
    '{message}\n' +
    '\n' +
    '本取引の詳細につきましては、貴社の以下の担当者様へ連絡しております。\n' +
    '\n' +
    '【貴社担当者様】\n' +
    '{contactDepartment}\n' +
    '{contact}様　（{contactEmail}）\n' +
    '\n' +
    '上記確認ができ次第、下記のURLから承諾のお手続きをお願いいたします。\n' +
    'https://sample.com\n' +
    '\n' +
    '◆お問合せ\n' +
    '本メールは自動送信メールの為、このメールへの返信はできません。\n' +
    '何かご質問やご不明な点がございましたら、下記の連絡先までお問い合わせください。\n' +
    '{contactInformationMessage}';

  useEffect(() => {
    const selected = recipient.at(0);
    let tmpPreviewMessage = previewMessage;
    tmpPreviewMessage = tmpPreviewMessage.replace(
      '{companyName}',
      inputOption.companyName ?? ''
    );
    tmpPreviewMessage = tmpPreviewMessage.replace(
      '{recipientName}',
      inputOption.tenantAdministrators.find((v) => v.email === selected?.value)
        ?.displayName ?? ''
    );
    if (message) {
      tmpPreviewMessage = tmpPreviewMessage.replace('{message}', message);
    } else {
      tmpPreviewMessage = tmpPreviewMessage.replace('{message}\n\n', '');
    }
    if (contactDepartment) {
      tmpPreviewMessage = tmpPreviewMessage.replace(
        '{contactDepartment}',
        contactDepartment
      );
    } else {
      tmpPreviewMessage = tmpPreviewMessage.replace(
        '{contactDepartment}\n',
        ''
      );
    }
    tmpPreviewMessage = tmpPreviewMessage.replace('{contact}', contact ?? '');
    tmpPreviewMessage = tmpPreviewMessage.replace(
      '{contactEmail}',
      contactEmail ?? ''
    );
    tmpPreviewMessage = tmpPreviewMessage.replace(
      '{contactInformationMessage}',
      contactInformationMessage ?? ''
    );
    setPreview(tmpPreviewMessage);
  }, [
    recipient,
    contact,
    contactEmail,
    contactDepartment,
    message,
    contactInformationMessage,
    previewMessage,
    inputOption.companyName,
    inputOption.tenantAdministrators,
  ]);

  useEffect(() => {
    setRecipient(administratorItems.slice(0, 1));
    setContact('');
    setContactEmail('');
    setContactDepartment('');
    setMessage('');
    setContactInformationMessage('');
    // isOpen 変更時だけ初期化したいのでlintから除外させる
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const isInputError = (): boolean => {
    const targetElm = document.querySelector('.relation-request-dialog');
    return includeInputValidateError(targetElm, intl, [
      {
        value: recipient.at(0)?.value ?? '',
        ref: requiredInputArea,
      },
      {
        value: contact,
        ref: requiredContactArea,
      },
      {
        value: contactEmail,
        ref: requiredContactEmailArea,
      },
      {
        value: contactInformationMessage,
        ref: requiredContactInformationMessageArea,
      },
    ]);
  };

  const sendMessage = {
    id: 'C0000001',
    value: {
      $1: GetMessageWithIntl(intl, { id: 'send' }),
    },
  };

  const handleCancel = () => {
    onCancel();
  };

  const handleDecision = () => {
    // user_id は承諾側テナントが email から解決するため詰めない
    const selected = inputOption.tenantAdministrators.find(
      (v) => v.email === recipient.at(0)?.value
    );
    onDecision({
      recipient: {
        email: selected?.email ?? '',
        displayName: selected?.displayName ?? '',
      },
      contact,
      contactEmail,
      contactDepartment,
      message,
      contactInformationMessage,
    });
    setSendConfirmOpen(false);
  };

  const view = (
    <div className="relation-request-dialog">
      <div className="description-area">
        <p className="text-box">
          <span className="text">
            {messageOption.messageLabelId &&
              GetMessageWithIntl(intl, messageOption.messageLabelId)}
          </span>
        </p>
      </div>
      <div className="input-line" ref={requiredInputArea}>
        <div className="item-group-100">
          <div className="w-100">
            <DataFilterbox
              name="recipient"
              labelId="RelationRequest.recipient"
              columns={['recipient']}
              data={administratorItems}
              value={recipient}
              validateOption={{ required: true }}
              onChangeState={setRecipient}
            />
          </div>
        </div>
      </div>
      <div className="input-line">
        <div className="item-group-100">
          <div className="w-50" ref={requiredContactArea}>
            <Textbox
              name="contact"
              labelId="RelationRequest.contact"
              type="text"
              columns={['contact']}
              value={contact}
              validateOption={{ required: true }}
              onChangeState={setContact}
            />
          </div>
          <div className="w-50" ref={requiredContactEmailArea}>
            <Textbox
              name="contactEmail"
              labelId="RelationRequest.contactEmail"
              type="email"
              columns={['contactEmail']}
              value={contactEmail}
              validator={validateEmail(intl, EMAIL_REG_EXP)}
              validateOption={{ required: true }}
              onChangeState={setContactEmail}
            />
          </div>
        </div>
      </div>
      <div className="input-line">
        <div className="item-group-100">
          <div className="w-50">
            <Textbox
              name="contactDepartment"
              labelId="RelationRequest.contactDepartment"
              type="text"
              columns={['contactDepartment']}
              value={contactDepartment}
              onChangeState={setContactDepartment}
            />
          </div>
        </div>
      </div>
      <div className="input-line">
        <div className="item-group-100">
          <div className="w-100">
            <Textarea
              name="message"
              labelId="RelationRequest.message"
              value={message}
              columns={['message']}
              className="w-100 mh-normal"
              isParentDeliver={true}
              onChangeState={setMessage}
            />
          </div>
        </div>
      </div>
      <div className="input-line" ref={requiredContactInformationMessageArea}>
        <div className="item-group-100">
          <div className="w-100">
            <Textarea
              name="contactInformationMessage"
              labelId="RelationRequest.contactInformationMessage"
              value={contactInformationMessage}
              columns={['contactInformationMessage']}
              className="w-100 mh-short"
              isParentDeliver={true}
              validateOption={{ required: true }}
              onChangeState={setContactInformationMessage}
            />
          </div>
        </div>
      </div>
      <div className="input-line">
        <div className="item-group-100">
          <div className="w-100">
            <Textarea
              name="preview"
              labelId="RelationRequest.preview"
              value={preview}
              columns={['preview']}
              className="w-100 mh-high"
              disabled={true}
            />
          </div>
        </div>
      </div>
      <div className="button-area">
        <CaptionButton
          name="cancelBtn"
          buttonType="cancel"
          className="button"
          caption={GetMessageWithIntl(intl, { id: 'cancel' })}
          onClick={() => handleCancel()}
        />
        <CaptionButton
          name="sendBtn"
          buttonType="high"
          className="button"
          caption={GetMessageWithIntl(intl, { id: 'send' })}
          onClick={() => {
            if (isInputError()) {
              return;
            }
            setSendConfirmOpen(true);
          }}
        />
      </div>
    </div>
  );

  const openModalProps: ModalDialogComponentProps = {
    cancel: () => {
      handleCancel();
    },
    send: () => {},
    modalIsOpen: isOpen,
    headerLabelId: messageOption.headerLabelId,
    messageLabelId: messageOption.messageLabelId ?? {},
    elements: view,
  };

  return (
    <>
      <ModalDialogComponent {...openModalProps} />
      {/* 送信 確認ダイアログ */}
      <ConfirmationDialog
        isOpen={isSendConfirmOpen}
        viewMessage={sendMessage}
        onDecision={() => {
          handleDecision();
        }}
        onCancel={() => {
          setSendConfirmOpen(false);
        }}
      />
    </>
  );
}
